import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import arrayMove from 'array-move';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom';

import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';

import useInput from '../../hook/useInput';

import InfoCollection from './InfoCollection/InfoCollection';
import ProductosCollection from './ProductsCollection/ProductosCollection';
import ImageCollection from './ImageCollection/ImageCollection';
import SubCollection from './SubCollection/SubCollection';
import CollectionAvailability from './CollectionAvailability/CollectionAvailability';
import DeleteDialog from '../DeleteConfirmDialog/DeleteConfirmDialog';

import { Container, ButtonContainer, Intro, MOBILE_STICKY_BOTTOM_INSET_PX } from './ManageCollection.styles';
import CollectionIcon from "../../assets/icons/collections.svg";
import { routes, errorToast } from '../../constants.js/misc';

/*SERVICES */
import {
    createCollectionsService, updateCollectionsService,
    collectionsxIdService, updateCollectionsServiceImage,
    deleteCollectionsServiceImage, deleteCollectionsService,
    createSubCollection, deleteSubCollection,
} from '../../services/collections';
import { persistSubcollectionOrder } from './SubCollection/sortableSubCollectionConfig';
import {
    buildCollectionUpdatePayload,
    getCollectionSortFields,
    applySavedSortToProducts,
    enrichCollectionWithSortPreference,
    persistCollectionSortPreference,
} from './collectionProductSort';

import { getProductsService } from '../../services/products';

const ManageCollection = ({ storeId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    let { id } = useParams();

    const [chipDataSubCollection, setChipDataSubCollection] = useState([]);
    const [loadingCollection, setLoadingCollection] = useState(false);
    const [dataCollection, setDataCollection] = useState({});
    const [listDataProduct, setListDataProduct] = useState([]);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelOptions, setCancelOptions] = useState(false);
    const [checkProductExists, setCheckProductExists] = useState(false);
    const [colleImg, setColleImg] = useState({});
    const [selectedImg, setSelectedImg] = useState([]);

    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 600px)' });
    const nameSubCollection = useInput('');
    const isEmptyFiles = obj => Object.keys(obj).length === 0;

    useEffect(() => {
        if (id) {
            setLoadingCollection(true);
            collectionsxIdService(storeId, id)
                .then(({ data }) => {
                    setLoadingCollection(false);
                    const enriched = enrichCollectionWithSortPreference(data, storeId);
                    setDataCollection(enriched);
                    if (data.picture_url) {
                        setColleImg({
                            preview: process.env.REACT_APP_IMAGES_API_URL + data.picture_url,
                            id: data.id
                        });
                    }
                    const subs = data.subcategories || data.sub_collections || [];
                    const sortedSubs = Array.isArray(subs)
                        ? [...subs].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
                        : [];
                    setChipDataSubCollection(sortedSubs);
                    setListDataProduct(applySavedSortToProducts(data.products || [], enriched));
                })
                .catch(err => {
                    setLoadingCollection(false);
                    enqueueSnackbar(t('Product.dataError'), {
                        variant: 'error',
                        autoHideDuration: 2500
                    });
                });
        }

    }, [id]);
    
    useEffect(() => {
        if (!checkProductExists) {
            getProductsService(storeId, 10, 0)
                .then(res => {
                    setCheckProductExists(res.data.length > 0 ? true : false);
                })
                .catch(err => {
                    enqueueSnackbar(t('ProductsTable.error'), {
                        variant: 'error',
                        autoHideDuration: 2500
                    })
                });
        }
    }, [storeId, checkProductExists]);

    const {
        control,
        formState,
        handleSubmit,
        formState: { errors },
        setValue,
        watch
    } = useForm({
        mode: 'all'
    });

    const isValid = Object.keys(errors)?.length === 0;

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex || !id) return;
        const reordered = arrayMove(listDataProduct, oldIndex, newIndex);
        setListDataProduct(reordered);

        const { sort_by } = getCollectionSortFields(dataCollection);
        const sortOverrides =
            sort_by !== 'manual' ? { sort_by: 'manual', sort_order: 'asc' } : {};

        updateCollectionsService(
            storeId,
            buildCollectionUpdatePayload(
                dataCollection,
                reordered.map((x) => x.id),
                sortOverrides
            )
        )
            .then(({ data }) => {
                const enriched = enrichCollectionWithSortPreference(data, storeId);
                setDataCollection(enriched);
                if (sortOverrides.sort_by) {
                    persistCollectionSortPreference(storeId, id, 'manual', 'asc');
                }
            })
            .catch((err) => console.log('Error updating collection order', err));
    };

    /**FUNCION PARA CONFIRMAR EL ELMINAR */
    const removeConfirmFunction = (value) => {
        setCancelOptions(value);
        setCancelDialogOpen(true);
    }

    /**FUNCION PARA ELMINAR UNA COLECCION */
    const removeCollection = () => {

        deleteCollectionsService(storeId, id, dataCollection)
            .then(({ data }) => {
                setCancelDialogOpen(false);
                history.push(routes.collectionsPage);
                enqueueSnackbar(`Colección eliminada exitosamente`, {
                    variant: 'success',
                    autoHideDuration: 2500
                });
            }).catch(err => {
                setCancelDialogOpen(false);
                enqueueSnackbar(t('Collection.deleteError'), errorToast);
            });
    }
    
    /**FUNCION PARA ELMINAR LA IMAGEN O ELIMINAR UNA COLECCION */
    const removeFunction = () => {
        if (cancelOptions.image) {
            deleteCollectionsServiceImage(storeId, id, dataCollection)
                .then(({ data }) => {
                    setCancelDialogOpen(false);
                    enqueueSnackbar(`Imagen de colección eliminada exitosamente`, {
                        variant: 'success',
                        autoHideDuration: 2500
                    });
                });
        } else {
            removeCollection();
        }
    }

    const handleDelete = (chipToDelete) => () => {
        const subId =
            typeof chipToDelete === 'object' && chipToDelete && chipToDelete.id
                ? chipToDelete.id
                : chipToDelete;
        setChipDataSubCollection((chips) =>
            chips.filter((chip) => {
                const cid = typeof chip === 'object' && chip && chip.id ? chip.id : chip;
                return cid !== subId;
            })
        );
        deleteSubCollection(storeId, subId)
            .then(() => {
                enqueueSnackbar(`Subcategoría eliminada exitosamente`, {
                    variant: 'success',
                    autoHideDuration: 2500
                });
            }).catch(err => {
                enqueueSnackbar(t('Collection.deleteError'), errorToast);
            });
    };

    const addHandlerSubCollection = () => {
        if (dataCollection.default) {
            return;
        }
        const name = (nameSubCollection.value || '').trim();
        if (name) {
            createSubCollection(storeId, id, name)
                .then(({ data: created }) => {
                    nameSubCollection.clearValue();
                    let nextSubs;
                    setChipDataSubCollection((chips) => {
                        nextSubs = [...chips, { id: created.id, name: created.name }];
                        return nextSubs;
                    });
                    return persistSubcollectionOrder(storeId, nextSubs).then(() => {
                        enqueueSnackbar(`Subcategoría creada exitosamente`, {
                            variant: 'success',
                            autoHideDuration: 2500
                        });
                    });
                }).catch(err => {
                    enqueueSnackbar(t('Collection.deleteError'), errorToast);
                });
        } else {
            enqueueSnackbar('Por favor añadir un nombre de subcategoría', errorToast);
        }
    }

    /**FUNCION PARA EDITAR O CREAR UNA COLECCION */
    const CallSubmit = (data) => {
        const functionToCall = id ? updateCollectionsService : createCollectionsService;
        setLoadingCollection(true);
        const { sort_by, sort_order } = getCollectionSortFields(dataCollection);
        if (id) {
            persistCollectionSortPreference(storeId, id, sort_by, sort_order);
        }
        functionToCall(storeId, {
            name: data.name,
            id: id ? id : null,
            description: data.description,
            public: data.active ? 1 : 0,
            sort_by,
            sort_order,
            sortBy: sort_by,
            sortOrder: sort_order,
            products: listDataProduct.map((x) => x.id),
        }).then(({ data }) => {
            if (!id) {
            }
            if (!isEmptyFiles(selectedImg)) {
                const imgData = new FormData();
                imgData.append('files', selectedImg[0]);
                updateCollectionsServiceImage(storeId, data.id, imgData)
                    .then(res => {
                        setLoadingCollection(false);
                        enqueueSnackbar(`Categoría ${id ? 'actualizada' : 'creada'} exitosamente`, {
                            variant: 'success',
                            autoHideDuration: 2500
                        });
                        history.push(routes.collectionsPage);
                    })
                    .catch(err => { });
            } else {
                setLoadingCollection(false);
                enqueueSnackbar(`Categoría ${id ? 'actualizada' : 'creada'} exitosamente`, {
                    variant: 'success',
                    autoHideDuration: 2500
                });
                isTabletOrMobile ? history.push(routes.mobileManageCategories) : history.push(routes.collectionsPage);
            }
        }).catch(err => console.log('error', err));
    }

    return (
        <>
            <form onSubmit={handleSubmit(CallSubmit)} style={{ paddingBottom: isTabletOrMobile ? MOBILE_STICKY_BOTTOM_INSET_PX : 0 }}>
                <Container container spacing={isTabletOrMobile ? 0 : 3}>
                    <Grid item container xs={12} style={{ padding: isTabletOrMobile ? '12px 16px 8px' : '0 0 20px' }}>
                        {isTabletOrMobile ? (
                            <Grid item xs={12}>
                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography
                                        variant="h6"
                                        style={{ fontWeight: 600, fontSize: 18, color: '#1E1E1E' }}
                                    >
                                        {id ? t('Collection.updateBtn') : t('Collection.createCollection')}
                                    </Typography>
                                    <CollectionAvailability
                                        collectionData={dataCollection}
                                        control={control}
                                        setValue={setValue}
                                        watch={watch}
                                        inline
                                    />
                                </Box>
                                <Link
                                    component={RouterLink}
                                    to={routes.mobileManageCategories}
                                    style={{ fontSize: 14 }}
                                >
                                    ‹ {t('ManageCollection.goHome')}
                                </Link>
                                {(id && !dataCollection.default) && (
                                    <Box mt={1.5}>
                                        <Button
                                            style={{ color: '#CB7C7A', paddingLeft: 0 }}
                                            onClick={() => removeConfirmFunction({ image: false })}
                                            size="small"
                                            color="inherit"
                                        >
                                            {t('Collection.deleteTitle')}
                                        </Button>
                                    </Box>
                                )}
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={6}>
                                    <Typography variant="h4" style={{ marginBottom: '10px' }}>
                                        {id ? t('Collection.updateBtn') : t('Collection.createCollection')}
                                    </Typography>
                                    <Link
                                        component={RouterLink}
                                        to={routes.collectionsPage}
                                    >
                                        {t('Collection.title')}
                                    </Link>
                                </Grid>
                                <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div>
                                        {(id && !dataCollection.default) && (
                                            <Button
                                                style={{ color: '#CB7C7A' }}
                                                onClick={() => removeConfirmFunction({ image: false })}
                                                size="small"
                                                color="inherit"
                                            >
                                                {t('Collection.deleteTitle')}
                                            </Button>
                                        )}
                                        {!id && (
                                            <Button
                                                size="small"
                                                component={RouterLink}
                                                to={routes.collectionsPage}
                                            >
                                                {t('Product.cancelButton')}
                                            </Button>
                                        )}
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            style={{ marginLeft: '16px' }}
                                            disabled={(id ? !isValid : !formState.isValid) || loadingCollection}
                                            type="submit"
                                        >
                                            {id ? t('Collection.saveChanges') : t('Collection.createCollection')}
                                        </Button>
                                    </div>
                                </Grid>
                            </>
                        )}
                    </Grid>


                    {loadingCollection && <Grid item xs={7}><LinearProgress /></Grid>}
                    <Grid
                        container
                        spacing={isTabletOrMobile ? 0 : 3}
                        style={isTabletOrMobile ? { backgroundColor: '#f5f5f5', paddingBottom: 24 } : undefined}
                    >
                        <Grid item xs={12} lg={8}>
                            <Box style={isTabletOrMobile ? { padding: '0 12px' } : undefined}>
                                <InfoCollection
                                    errors={errors}
                                    dataCollection={dataCollection}
                                    control={control}
                                    setValue={setValue}
                                    isMobile={isTabletOrMobile}
                                />
                                {id &&
                                    !loadingCollection &&
                                    !dataCollection.default && (
                                        <SubCollection
                                            handleDelete={handleDelete}
                                            nameSubCollection={nameSubCollection}
                                            addHandlerSubCollection={addHandlerSubCollection}
                                            chipDataSubCollection={chipDataSubCollection}
                                            setChipDataSubCollection={setChipDataSubCollection}
                                            listDataProduct={listDataProduct}
                                            setListDataProduct={setListDataProduct}
                                            storeId={storeId}
                                            parentId={id}
                                            isMobile={isTabletOrMobile}
                                        />
                                    )}
                                {checkProductExists && (
                                    <ProductosCollection
                                        onSortEnd={onSortEnd}
                                        collectionId={id}
                                        collectionData={dataCollection}
                                        listDataProduct={listDataProduct}
                                        setListDataProduct={setListDataProduct}
                                        setDataCollection={setDataCollection}
                                    />
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} lg={4}>
                            <Box style={isTabletOrMobile ? { padding: '0 12px' } : undefined}>
                                <ImageCollection
                                    removeImgFunction={() => removeConfirmFunction({ image: true })}
                                    setColleImg={setColleImg}
                                    setSelectedImg={setSelectedImg}
                                    colleImg={colleImg}
                                />
                                {!isTabletOrMobile && (
                                    <CollectionAvailability
                                        collectionData={dataCollection}
                                        control={control}
                                        setValue={setValue}
                                        watch={watch}
                                    />
                                )}
                            </Box>
                        </Grid>

                        {isTabletOrMobile &&
                            <ButtonContainer
                                item
                                xs={12}
                                lg={4}>
                                <Button
                                    style={{ width: '100%', padding: '12px 0', fontWeight: 600, fontSize: 15 }}
                                    color="primary"
                                    variant="contained"
                                    disabled={(id ? !isValid : !formState.isValid) || loadingCollection}
                                    type="submit">
                                    {id ? t('Collection.saveChanges') : t('Collection.createCollection')}
                                </Button>
                            </ButtonContainer>
                        }
                    </Grid>
                </Container>

                {id &&
                    <DeleteDialog
                        open={cancelDialogOpen}
                        onConfirm={removeFunction}
                        onClose={() => setCancelDialogOpen(false)}
                        title={cancelOptions.image ? t('Collection.deleteImageTitle') : t('Collection.deleteTitle')}
                        message={cancelOptions.image ? t('Product.deleteImageMessage') : t('Collection.deleteMessage')}
                        cancelText={t('Product.deleteCancel')}
                        confirmText={t('Product.deleteConfirm')}
                    />
                }

            </form>

        </>
    );
};

const mapStateToProps = ({ auth }) => ({
    storeId: auth.storeId
});


export default connect(
    mapStateToProps
)(ManageCollection);